import { useMutation } from '@tanstack/react-query';
import { useApolloClient } from '@apollo/client/react';
import { updateFlow as updateFlowAPI } from '../../api/remiApi';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { Flow } from '../useFlowsData';

interface UpdateFlowData extends Partial<Flow> {
  id: string;
}

/**
 * Map frontend UpdateFlowData fields to Remi API snake_case schema.
 */
const mapToApiPayload = (updateData: Omit<UpdateFlowData, 'id'>): Record<string, unknown> => {
  const raw = updateData as Record<string, unknown>;
  const payload: Record<string, unknown> = {};

  if (raw.name !== undefined) {
    payload.name = String(raw.name).trim();
  }

  // Direct snake_case passthrough fields
  const passthroughFields = [
    'description', 'description_id', 'flow_type', 'sub_type', 'status',
    'scheduled_for', 'timezone', 'country_code', 'cadence',
    'schedule_over_unit', 'scheduling_window', 'allowed_hours',
    'allowed_days', 'exclude_holidays', 'transfer_numbers',
    'lead_source', 'lead_email', 'inbound', 'max_attempts',
    'rejoin', 'workflow_type',
  ];

  for (const field of passthroughFields) {
    if (raw[field] !== undefined) payload[field] = raw[field];
  }

  // camelCase → snake_case
  if (raw.useRoundRobin !== undefined) payload.use_round_robin = raw.useRoundRobin;

  // voicemail text content: frontend uses 'voicemail' key, API uses 'voicemail_content'
  if (raw.voicemail !== undefined) payload.voicemail_content = raw.voicemail;
  if (raw.voicemailContent !== undefined) payload.voicemail_content = raw.voicemailContent;

  return payload;
};

export const useUpdateFlow = () => {
  const apolloClient = useApolloClient();
  const { organization } = useOrganizationStore();

  const updateFlow = async (data: UpdateFlowData) => {
    if (!organization?.id) {
      throw new Error('Organization ID is required');
    }

    const { id, ...updateData } = data;
    const payload = mapToApiPayload(updateData);

    await updateFlowAPI(organization.id, id, payload);
    return data;
  };

  const mutation = useMutation({
    mutationFn: updateFlow,
    onSuccess: () => {
      // Refetch all GetFlows queries so the list reflects the updated flow
      apolloClient.refetchQueries({ include: ['GetFlows'] });
    },
  });

  return {
    updateFlow: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
};
